import { useState } from "react";
import { Mail, Phone, MapPin, Send, MessageCircle, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { BRAND, OFFICES, SERVICE_OPTIONS } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_TYPES = ["General Enquiry", "Free Site Survey", "Request a Quote"];

const initial = { name: "", company: "", email: "", phone: "", service: "", message: "", website: "" };

const inputCls =
  "focus-ring w-full rounded-xl border border-slate-700 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#2563EB]";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [formType, setFormType] = useState(FORM_TYPES[0]);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/enquiry`, { ...form, form_type: formType });
      toast.success(res.data.message || "Message sent!");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappHref = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent("Hi KAS Info Tech! I'd like to discuss a security project.")}`;

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        titleLines={["Let's secure your", "infrastructure."]}
        subtitle="Request a free site survey, ask for a quote, or just say hello. Our team responds within one business day — or reach us instantly on WhatsApp."
        imageKey="server_room"
      />

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
        <div className="grid gap-12 lg:grid-cols-5 lg:gap-16">
          {/* Form */}
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="relative rounded-3xl border border-slate-800 bg-[#111c33]/40 p-8 lg:p-10">
              {/* honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />

              {/* form type toggle */}
              <div className="mb-8 flex flex-wrap gap-2" data-testid="form-type-toggle">
                {FORM_TYPES.map((ft) => (
                  <button key={ft} type="button" onClick={() => setFormType(ft)} data-testid={`form-type-${ft.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${formType === ft ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                    {ft}
                  </button>
                ))}
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Name *"><input required type="text" value={form.name} onChange={update("name")} data-testid="contact-input-name" placeholder="Your full name" className={inputCls} /></Field>
                <Field label="Company / Organisation"><input type="text" value={form.company} onChange={update("company")} data-testid="contact-input-company" placeholder="Company name" className={inputCls} /></Field>
                <Field label="Email *"><input required type="email" value={form.email} onChange={update("email")} data-testid="contact-input-email" placeholder="you@company.com" className={inputCls} /></Field>
                <Field label="Phone"><input type="tel" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" placeholder="+91 98765 43210" className={inputCls} /></Field>
              </div>

              <div className="mt-6">
                <Field label="Service Interest">
                  <select value={form.service} onChange={update("service")} data-testid="contact-select-service" className={`${inputCls} appearance-none`}>
                    <option value="" className="bg-[#111c33]">Select a service…</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#111c33]">{s}</option>)}
                  </select>
                </Field>
              </div>

              <div className="mt-6">
                <Field label="Message *"><textarea required rows={5} value={form.message} onChange={update("message")} data-testid="contact-input-message" placeholder="Tell us about your requirement, site details and timeline…" className={`${inputCls} resize-none`} /></Field>
              </div>

              <button type="submit" disabled={loading} data-testid="contact-submit-button" className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-[#3b82f6] disabled:opacity-60 sm:w-auto">
                {loading ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>) : (<>Send {formType} <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
              </button>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="space-y-4">
              <a href={`tel:${BRAND.phoneRaw}`} data-testid="contact-hotline" className="flex items-center gap-4 rounded-2xl border border-[#2563EB]/40 bg-[#2563EB]/10 p-5 transition-colors hover:bg-[#2563EB]/20">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB] text-white"><Clock className="h-5 w-5" /></span>
                <div><p className="text-xs uppercase tracking-widest text-[#60A5FA]">24/7 Support Hotline</p><p className="mt-0.5 font-display text-lg font-bold text-white">{BRAND.supportLine}</p></div>
              </a>

              {[
                { Icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
                { Icon: Phone, label: "Phone", value: BRAND.phone, href: `tel:${BRAND.phoneRaw}` },
              ].map(({ Icon, label, value, href }) => (
                <a key={label} href={href} data-testid={`contact-info-${label.toLowerCase()}`} className="flex items-center gap-4 rounded-2xl border border-slate-800 bg-[#111c33]/40 p-5 transition-colors hover:border-slate-700">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]/15 text-[#60A5FA]"><Icon className="h-5 w-5" /></span>
                  <div><p className="text-xs uppercase tracking-widest text-slate-500">{label}</p><p className="mt-0.5 text-white">{value}</p></div>
                </a>
              ))}

              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/10 p-5 transition-colors hover:bg-[#25D366]/20">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366] text-white"><MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} /></span>
                <div><p className="text-xs uppercase tracking-widest text-[#25D366]">Instant chat</p><p className="mt-0.5 font-semibold text-white">Message us on WhatsApp</p></div>
              </a>

              {/* Offices */}
              <div className="rounded-2xl border border-slate-800 bg-[#111c33]/40 p-5" data-testid="offices-list">
                <p className="mb-3 text-xs uppercase tracking-widest text-slate-500">Our offices</p>
                <ul className="space-y-3">
                  {OFFICES.map((o) => (
                    <li key={o.city} className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]" />
                      <div><p className="font-medium text-white">{o.city}</p><p className="text-sm text-slate-400">{o.address}</p></div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Map */}
        <Reveal>
          <div className="mt-12 overflow-hidden rounded-3xl border border-slate-800" data-testid="contact-map">
            <iframe title="KAS Info Tech head office" src="https://www.google.com/maps?q=Jaipur,Rajasthan,India&z=11&output=embed" width="100%" height="360" style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(20%)" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-slate-300">{label}</span>
      {children}
    </label>
  );
}
